import { useState } from 'react';
import { Modal } from './Modal';

/**
 * Report-a-peer modal. Replaces the plain window.prompt with a guided
 * reason picker — the categories match the moderator's review queue so
 * the report lands with an actionable, structured signal instead of free
 * text alone. An optional "anything else" textarea is always available.
 *
 * The categories deliberately omit "self_harm" — the moderation cascade
 * detects that automatically and surfaces 988 to the sender; making it
 * a report option here would suggest peers should triage crises, which
 * the safety doc explicitly says they should not (detect and refer).
 */

interface Reason {
  id: string;
  label: string;
  description: string;
}

const REASONS: Reason[] = [
  {
    id: 'harassment',
    label: 'Harassment or threats',
    description: 'Insults, intimidation, slurs, or anything menacing.',
  },
  {
    id: 'sexual',
    label: 'Sexual content',
    description: 'Sexual messages or requests, even if seemingly playful.',
  },
  {
    id: 'minor',
    label: 'Concerning content about a minor',
    description: 'Anything that makes you think the other person might be under 18.',
  },
  {
    id: 'money_or_off_platform',
    label: 'Money or off-platform contact',
    description: 'Asking for money, gifts, or to move to WhatsApp / phone / etc.',
  },
  {
    id: 'spam',
    label: 'Spam or a bot',
    description: 'Doesn’t feel like a real person, or repetitive sales-y content.',
  },
  {
    id: 'other',
    label: 'Something else',
    description: 'I’ll explain in a sentence below.',
  },
];

export function ReportModal({
  onSubmit,
  onClose,
}: {
  onSubmit: (reason: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  onClose: () => void;
}) {
  const [reasonId, setReasonId] = useState<string>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const reason = REASONS.find((r) => r.id === reasonId);
  const noteRequired = reasonId === 'other';
  const canSubmit = Boolean(reason) && (!noteRequired || note.trim().length > 0);

  async function submit() {
    if (!reason) return;
    const composed =
      reasonId === 'other'
        ? note.trim()
        : `${reason.label}${note.trim() ? ` — ${note.trim()}` : ''}`;
    setBusy(true);
    setError(null);
    const result = await onSubmit(composed);
    if (result.ok) {
      onClose();
    } else {
      setError(result.error);
      setBusy(false);
      setConfirming(false);
    }
  }

  if (confirming && reason) {
    const trimmedNote = note.trim();
    return (
      <Modal title="Send this report?" onClose={onClose}>
        <p className="muted">
          A moderator will review the conversation. Your peer will not see this report or know it
          was you.
        </p>
        <div className="report-confirm-summary">
          <div className="report-confirm-label">Reason</div>
          <div className="report-confirm-value">{reason.label}</div>
          {trimmedNote && (
            <>
              <div className="report-confirm-label">Note</div>
              <div className="report-confirm-value">{trimmedNote}</div>
            </>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" disabled={busy} onClick={submit}>
            {busy ? 'Sending…' : 'Yes, send report'}
          </button>
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={() => setConfirming(false)}
          >
            Go back
          </button>
        </div>
        {error && <p className="error">{error}</p>}
      </Modal>
    );
  }

  return (
    <Modal title="Report this conversation" onClose={onClose}>
      <p className="muted">
        Your report goes to a moderator. Your peer will not see it or know it was you. We act on
        every report.
      </p>
      <fieldset className="reasons">
        <legend className="visually-hidden">What happened?</legend>
        {REASONS.map((r) => (
          <label
            key={r.id}
            className={`reason${reasonId === r.id ? ' on' : ''}`}
            htmlFor={`reason-${r.id}`}
          >
            <input
              id={`reason-${r.id}`}
              type="radio"
              name="report-reason"
              value={r.id}
              checked={reasonId === r.id}
              onChange={() => setReasonId(r.id)}
              disabled={busy}
            />
            <span className="reason-body">
              <span className="reason-label">{r.label}</span>
              <span className="reason-desc">{r.description}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <textarea
        className="modal-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={1000}
        placeholder={
          noteRequired
            ? 'Please tell us what happened.'
            : 'Anything else a moderator should know? (optional)'
        }
        rows={3}
        disabled={busy}
      />
      <div className="modal-actions">
        <button type="button" disabled={!canSubmit || busy} onClick={() => setConfirming(true)}>
          Continue
        </button>
        <button type="button" className="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </div>
      {error && <p className="error">{error}</p>}
    </Modal>
  );
}
